import { Link } from 'react-router-dom'
import { 
  GraduationCap, 
  MessageCircle, 
  Bell, 
  TrendingUp,
  Calendar,
  Award,
  ArrowLeft,
  User,
  FileText
} from 'lucide-react'

export default function ParentDemo() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Demo Banner */}
      <div className="bg-blue-600 text-white py-3 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium">Demo Mode</span>
            <span className="text-sm">You're viewing the Parent dashboard</span>
          </div>
          <Link to="/" className="text-sm hover:underline flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </div>

      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-teal-600 rounded-xl flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">SchoolConnect</span>
            </div>
            <div className="flex items-center gap-4">
              <Bell className="w-5 h-5 text-gray-500" />
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-700 font-semibold text-sm">MC</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Welcome, Michael!</h1>
          <p className="text-gray-600">Parent of Emma Johnson (Grade 5B)</p>
        </div>

        {/* Child Profile Card */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-8">
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 bg-gradient-to-br from-teal-400 to-teal-600 rounded-2xl flex items-center justify-center">
              <User className="w-10 h-10 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900">Emma Johnson</h2>
              <p className="text-gray-600">Grade 5B • Mrs. Johnson's Class</p>
              <div className="flex items-center gap-4 mt-3">
                <span className="bg-teal-50 text-teal-700 px-3 py-1 rounded-full text-sm font-medium">
                  450 Points
                </span>
                <span className="bg-yellow-50 text-yellow-700 px-3 py-1 rounded-full text-sm font-medium">
                  ⭐ Top Performer
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 text-center">
                <TrendingUp className="w-6 h-6 text-teal-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-gray-900">85%</p>
                <p className="text-sm text-gray-500">Average Score</p>
              </div>
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 text-center">
                <Award className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-gray-900">12</p>
                <p className="text-sm text-gray-500">Badges Earned</p>
              </div>
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 text-center">
                <Calendar className="w-6 h-6 text-orange-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-gray-900">98%</p>
                <p className="text-sm text-gray-500">Attendance</p>
              </div>
            </div>

            {/* Recent Achievements */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Achievements</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-yellow-50 rounded-xl">
                  <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Award className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Helper Badge Earned!</p>
                    <p className="text-sm text-gray-500">Emma helped a classmate with math homework</p>
                    <p className="text-xs text-gray-400 mt-1">+50 points • 2 days ago</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-xl">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Science Project Submitted</p>
                    <p className="text-sm text-gray-500">"The Solar System" project completed</p>
                    <p className="text-xs text-gray-400 mt-1">Grade: A • 3 days ago</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-green-50 rounded-xl">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Math Test Improvement</p>
                    <p className="text-sm text-gray-500">Score improved from 75% to 92%</p>
                    <p className="text-xs text-gray-400 mt-1">+30 points • 1 week ago</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Messages from Teachers</h2>
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-medium text-gray-900">Mrs. Johnson</p>
                    <span className="text-xs text-gray-400">Today</span>
                  </div>
                  <p className="text-sm text-gray-600">Emma has been doing excellent work in class this week. She's been very helpful to her classmates!</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-medium text-gray-900">Mr. Smith (Math)</p>
                    <span className="text-xs text-gray-400">Yesterday</span>
                  </div>
                  <p className="text-sm text-gray-600">Don't forget about the math quiz on Friday. Emma is well prepared!</p>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Upcoming Events */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Events</h2>
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-red-50 rounded-xl">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-red-700 font-bold text-sm">15</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Parent-Teacher Meeting</p>
                    <p className="text-sm text-gray-500">3:00 PM - 5:00 PM</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-xl">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-700 font-bold text-sm">18</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Field Trip</p>
                    <p className="text-sm text-gray-500">Science Museum</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-green-50 rounded-xl">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-green-700 font-bold text-sm">22</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Sports Day</p>
                    <p className="text-sm text-gray-500">All day event</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
              <div className="space-y-3">
                <button className="w-full p-3 bg-teal-50 rounded-xl text-left hover:bg-teal-100 transition-colors">
                  <MessageCircle className="w-5 h-5 text-teal-600 mb-1" />
                  <p className="font-medium text-gray-900 text-sm">Message Teacher</p>
                </button>
                <button className="w-full p-3 bg-blue-50 rounded-xl text-left hover:bg-blue-100 transition-colors">
                  <FileText className="w-5 h-5 text-blue-600 mb-1" />
                  <p className="font-medium text-gray-900 text-sm">View Report Card</p>
                </button>
                <button className="w-full p-3 bg-purple-50 rounded-xl text-left hover:bg-purple-100 transition-colors">
                  <Award className="w-5 h-5 text-purple-600 mb-1" />
                  <p className="font-medium text-gray-900 text-sm">View Portfolio</p>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-blue-900 py-12 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Stay connected with your child's education</h2>
          <p className="text-blue-100 mb-6">Join thousands of parents already using SchoolConnect Hub</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register-school" className="bg-white text-blue-900 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors">
              Start 14-Day Free Trial
            </Link>
            <Link to="/demo/student" className="bg-white/10 text-white px-8 py-3 rounded-full font-semibold hover:bg-white/20 transition-colors">
              See Student View
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
